import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, finalize, map, of, shareReplay, take } from 'rxjs';
import { ConfirmDialogService } from '../../shared/overlay/confirm-dialog/confirm-dialog.service';

export interface PendingChangesRegistration {
  readonly isDirty: () => boolean;
  readonly discard: () => void;
}

@Injectable({ providedIn: 'root' })
export class PendingChangesService {
  private readonly confirmDialog = inject(ConfirmDialogService);
  private active?: PendingChangesRegistration;
  private confirmation?: Observable<boolean>;

  constructor() {
    const browserWindow = inject(DOCUMENT).defaultView;
    const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!this.hasPendingChanges()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    browserWindow?.addEventListener('beforeunload', warnBeforeUnload);
    inject(DestroyRef).onDestroy(() => browserWindow?.removeEventListener('beforeunload', warnBeforeUnload));
  }

  register(registration: PendingChangesRegistration): () => void {
    this.active = registration;
    return () => {
      if (this.active === registration) this.active = undefined;
    };
  }

  hasPendingChanges(): boolean {
    return this.active?.isDirty() === true;
  }

  confirmDiscard(): Observable<boolean> {
    const registration = this.active;
    if (registration === undefined || !registration.isDirty()) return of(true);
    if (this.confirmation) return this.confirmation;

    this.confirmation = this.confirmDialog.confirm({
      title: 'Cambios sin guardar',
      message: 'Tienes cambios sin guardar. Si sales ahora, se perderán.',
      confirmText: 'Descartar cambios',
      cancelText: 'Continuar editando',
      severity: 'warning',
      closeOnNavigation: false,
    }).pipe(
      take(1),
      map((confirmed) => {
        if (confirmed && this.active === registration) {
          this.active = undefined;
          registration.discard();
        }
        return confirmed;
      }),
      finalize(() => { this.confirmation = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.confirmation;
  }
}
